import { useState, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Plus, Pencil, ToggleLeft, ToggleRight, X, ExternalLink, Upload, Trash2, AlertTriangle, FileBarChart } from 'lucide-react'
import { Link as RouterLink } from 'react-router-dom'
import CsvControls from '../components/CsvControls'
import { useSearchParams } from 'react-router-dom'
import { api } from '../lib/api'
import { formatCurrency, parseUtc } from '../lib/utils'
import { useAuth } from '../context/AuthContext'

const FILES_URL = 'https://files.drinx.jo'

interface Product {
  id: string; sku: string; barcode: string | null; name: string; name_ar: string | null; brand: string | null
  price_fils: number; cost_fils: number; category_id: string; category_name: string | null
  stock: number; active: number; image_key: string | null; size_ml: number | null
  description: string | null; description_ar: string | null; alcohol_pct: number | null
  tasting_notes_en: string | null; tasting_notes_ar: string | null
  variant_group_id: string | null; is_primary_variant: number; is_house_brand: number
  // Human-edit trail (migration 0145). edited_at bumped by admin POST /
  // PUT / CSV-import; edited_by_admin_id set to the admin who did the
  // write. Till-created rows carry edited_at but a NULL admin id → the
  // list renders "till" for those. Pre-migration rows have edited_at =
  // created_at as a backfill floor.
  edited_at: string | null
  edited_by_admin_id: string | null
  edited_by_email: string | null
  // Per-category attributes (migration 0150). Values keyed by the
  // schema field names on the category. Null / absent when the
  // category has no schema OR the product hasn't been filled in.
  attributes_json: string | null
  category_attribute_schema_json: string | null
}

interface Category { id: string; name: string; attribute_schema_json?: string | null }

// Attribute schema shape (from `categories.attribute_schema_json`).
// Matches the seed in migration 0150.
interface AttributeField {
  key: string
  label_en: string
  label_ar: string
  type: 'enum' | 'number' | 'text'
  filterable?: boolean
  options?: string[]
}

const schema = z.object({
  // SKU: server-generated on create when blank (ADM-<hex>). Editable
  // afterwards — DB enforces uniqueness, worker returns duplicate_sku
  // (409) on collision. Blank on Save leaves the existing sku untouched
  // (see PUT fieldMap filter). Warn text in the modal calls out the
  // side effects (POS scanner + receipt lookups key on sku).
  sku: z.string().optional(),
  name: z.string().min(1, 'Required'),
  name_ar: z.string().optional(),
  brand: z.string().optional(),
  // EAN-13 / UPC-A printed on the bottle. Captured when ops adds the
  // product so the till BT scanner can resolve a scan to a SKU. Optional
  // (some products won't have a scannable label, e.g. gift boxes).
  barcode: z.string().optional(),
  price_fils: z.number().int().min(1, 'Required'),
  // Weighted-average cost (fils). Populated by the PO receive flow on
  // the ERP side; also editable here for manual corrections. Feeds the
  // margin calc + AP aging surface. 0 = unknown (don't reject).
  cost_fils: z.number().int().min(0).optional(),
  category_id: z.string().min(1, 'Required'),
  stock: z.number().int().min(0),
  size_ml: z.number().optional(),
  alcohol_pct: z.number().min(0).max(100).optional(),
  description: z.string().optional(),
  description_ar: z.string().optional(),
  tasting_notes_en: z.string().optional(),
  tasting_notes_ar: z.string().optional(),
  image_key: z.string().optional(),
  variant_group_id: z.string().optional(),
  is_primary_variant: z.boolean().optional(),
  is_house_brand: z.boolean().optional(),
})
type FormData = z.infer<typeof schema>

export default function Products() {
  const { can } = useAuth()
  const [searchParams] = useSearchParams()
  const [search, setSearch] = useState('')
  const [categoryFilter, setCategoryFilter] = useState(() => searchParams.get('category') ?? '')
  const [page, setPage] = useState(1)
  const [sortCol, setSortCol] = useState('created_at')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
  const [houseFilter, setHouseFilter] = useState<'all' | 'house' | 'other'>('all')
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all')
  // Ops reconciliation filter — "which SKUs did we edit in the last N?"
  // Backend accepts presets (today / yesterday / 7d / 30d) or an ISO
  // date; UI exposes the presets. 'all' turns the filter off.
  const [editedFilter, setEditedFilter] = useState<'all' | 'today' | 'yesterday' | '7d' | '30d'>('all')
  const [modalProduct, setModalProduct] = useState<Product | null | 'new'>(null)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [confirmDelete, setConfirmDelete] = useState<Product[] | null>(null)
  const qc = useQueryClient()

  function toggleSelected(id: string) {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }
  function clearSelection() { setSelectedIds(new Set()) }

  function handleSort(col: string) {
    if (sortCol === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortCol(col)
      setSortDir('asc')
    }
    setPage(1)
  }

  const params = new URLSearchParams()
  if (search) params.set('search', search)
  if (categoryFilter) params.set('category', categoryFilter)
  if (houseFilter !== 'all') params.set('house_brand', houseFilter)
  if (activeFilter !== 'all') params.set('active', activeFilter)
  if (editedFilter !== 'all') params.set('edited_since', editedFilter)
  params.set('sort', sortCol)
  params.set('order', sortDir)
  params.set('page', String(page))

  const { data } = useQuery<{ products: Product[]; total: number; limit: number }>({
    queryKey: ['products', search, categoryFilter, houseFilter, activeFilter, editedFilter, page, sortCol, sortDir],
    queryFn: () => api.get(`/products?${params}`).then(r => r.data),
  })

  // Filter-only query string for the CSV export — same params, minus
  // page (export ignores pagination and returns all filtered rows).
  const exportParams = new URLSearchParams()
  if (search) exportParams.set('search', search)
  if (categoryFilter) exportParams.set('category', categoryFilter)
  if (houseFilter !== 'all') exportParams.set('house_brand', houseFilter)
  if (activeFilter !== 'all') exportParams.set('active', activeFilter)
  if (editedFilter !== 'all') exportParams.set('edited_since', editedFilter)
  exportParams.set('sort', sortCol)
  exportParams.set('order', sortDir)
  const exportQuery = exportParams.toString() ? `?${exportParams}` : ''

  const { data: cats } = useQuery<{ categories: Category[] }>({
    queryKey: ['categories'],
    queryFn: () => api.get('/categories').then(r => r.data),
  })

  const toggleActive = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      api.put(`/products/${id}`, { active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['products'] }),
  })

  const toggleHouseBrand = useMutation({
    mutationFn: ({ id, is_house_brand }: { id: string; is_house_brand: boolean }) =>
      api.put(`/products/${id}`, { is_house_brand }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['products'] }),
  })

  const totalPages = Math.ceil((data?.total ?? 0) / (data?.limit ?? 50))
  const canEdit = can(['super_admin', 'operations'])
  const canDelete = canEdit  // matches worker RBAC on DELETE + bulk-delete

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-lg font-semibold text-white">Products</h1>
        {canEdit && (
          <div className="flex items-center gap-2">
            <CsvControls
              basePath="/products"
              templateFilename="products-template.csv"
              exportFilename={`products-export-${new Date().toISOString().slice(0, 10)}.csv`}
              label="product"
              onImported={() => qc.invalidateQueries({ queryKey: ['products'] })}
              exportQuery={exportQuery}
            />
            <button
              onClick={() => setModalProduct('new')}
              className="flex items-center gap-2 bg-[#C41E3A] hover:bg-[#a51830] text-white text-sm px-4 py-2 rounded-lg transition-colors"
            >
              <Plus size={15} /> Add Product
            </button>
          </div>
        )}
      </div>

      <div className="flex gap-3 mb-5">
        <input
          value={search} onChange={e => { setSearch(e.target.value); setPage(1) }}
          placeholder="Search products…"
          className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#C41E3A] w-64"
        />
        <select
          value={categoryFilter} onChange={e => { setCategoryFilter(e.target.value); setPage(1) }}
          className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C41E3A]"
        >
          <option value="">All categories</option>
          {cats?.categories
            .slice()
            .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
            .map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select
          value={houseFilter}
          onChange={e => { setHouseFilter(e.target.value as 'all' | 'house' | 'other'); setPage(1) }}
          className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C41E3A]"
          title="Filter by house-brand status"
        >
          <option value="all">All brands</option>
          <option value="house">House brand (YHC / Fursan)</option>
          <option value="other">Other brands</option>
        </select>
        <select
          value={activeFilter}
          onChange={e => { setActiveFilter(e.target.value as 'all' | 'active' | 'inactive'); setPage(1) }}
          className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C41E3A]"
          title="Filter by active/inactive"
        >
          <option value="all">All (active + inactive)</option>
          <option value="active">Active only</option>
          <option value="inactive">Inactive only</option>
        </select>
        <select
          value={editedFilter}
          onChange={e => { setEditedFilter(e.target.value as 'all' | 'today' | 'yesterday' | '7d' | '30d'); setPage(1) }}
          className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C41E3A]"
          title="Filter by when the SKU was last edited by a human (admin edit or till create — not sales / stock deltas)"
        >
          <option value="all">Edited anytime</option>
          <option value="today">Edited today</option>
          <option value="yesterday">Edited since yesterday</option>
          <option value="7d">Edited in last 7 days</option>
          <option value="30d">Edited in last 30 days</option>
        </select>
      </div>

      {canDelete && selectedIds.size > 0 && (
        <div className="mb-4 flex items-center justify-between rounded-lg border border-[#c41e3a]/40 bg-[#c41e3a]/10 px-4 py-2.5">
          <div className="text-sm text-white">
            <span className="font-semibold">{selectedIds.size}</span> selected
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={clearSelection}
              className="text-xs text-gray-400 hover:text-white px-3 py-1.5 rounded-md"
            >
              Clear
            </button>
            <button
              onClick={() => {
                const selected = (data?.products ?? []).filter(p => selectedIds.has(p.id))
                if (selected.length > 0) setConfirmDelete(selected)
              }}
              className="flex items-center gap-1.5 bg-[#C41E3A] hover:bg-[#a01830] text-white text-xs font-semibold px-3 py-1.5 rounded-md"
            >
              <Trash2 size={12} /> Delete {selectedIds.size} {selectedIds.size === 1 ? 'product' : 'products'}
            </button>
          </div>
        </div>
      )}

      <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-gray-600 border-b border-[#2e2e2e]">
              {canDelete && (
                <th className="px-3 py-3 w-8">
                  <input
                    type="checkbox"
                    checked={(data?.products ?? []).length > 0 && (data?.products ?? []).every(p => selectedIds.has(p.id))}
                    onChange={e => {
                      if (e.target.checked) {
                        setSelectedIds(new Set((data?.products ?? []).map(p => p.id)))
                      } else {
                        clearSelection()
                      }
                    }}
                    className="accent-[#C41E3A]"
                    title="Select all on this page"
                  />
                </th>
              )}
              <th className="px-3 py-3 w-14"></th>
              <SortTh label="Name"     col="name"       current={sortCol} dir={sortDir} onSort={handleSort} />
              <SortTh label="Brand"    col="brand"      current={sortCol} dir={sortDir} onSort={handleSort} />
              <SortTh label="Category" col="category"   current={sortCol} dir={sortDir} onSort={handleSort} />
              <SortTh label="Price"    col="price"      current={sortCol} dir={sortDir} onSort={handleSort} />
              <SortTh label="Size"     col="size"       current={sortCol} dir={sortDir} onSort={handleSort} />
              <SortTh label="Stock"    col="stock"      current={sortCol} dir={sortDir} onSort={handleSort} />
              <SortTh label="House"    col="house"      current={sortCol} dir={sortDir} onSort={handleSort} />
              <SortTh label="Active"   col="active"     current={sortCol} dir={sortDir} onSort={handleSort} />
              <SortTh label="Last edited" col="edited_at" current={sortCol} dir={sortDir} onSort={handleSort} />
              <th className="px-4 py-3 w-8"></th>
            </tr>
          </thead>
          <tbody>
            {data?.products.map((p) => (
              <tr key={p.id} className={`border-b border-[#222] hover:bg-white/[0.02] transition-colors ${selectedIds.has(p.id) ? 'bg-[#c41e3a]/5' : ''}`}>
                {canDelete && (
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(p.id)}
                      onChange={() => toggleSelected(p.id)}
                      className="accent-[#C41E3A]"
                    />
                  </td>
                )}
                <td className="px-3 py-2">
                  {p.image_key ? (
                    <a href={`${FILES_URL}/${p.image_key}`} target="_blank" rel="noreferrer" title="Open full image">
                      <img
                        src={`${FILES_URL}/${p.image_key}`}
                        alt=""
                        className="w-10 h-10 rounded-lg object-cover bg-[#2e2e2e]"
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                      />
                    </a>
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-[#2e2e2e]" />
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <p className="text-white">{p.name}</p>
                    {p.variant_group_id && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${p.is_primary_variant ? 'bg-blue-900/50 text-blue-300' : 'bg-[#2e2e2e] text-gray-500'}`}>
                        {p.is_primary_variant ? 'primary' : 'variant'}
                      </span>
                    )}
                  </div>
                  {p.sku && <p className="text-gray-500 text-[11px] font-mono mt-0.5">{p.sku}</p>}
                  {p.name_ar && <p className="text-gray-500 text-xs">{p.name_ar}</p>}
                  {p.description && (
                    <p className="text-gray-600 text-xs mt-0.5 line-clamp-1 max-w-xs">{p.description}</p>
                  )}
                </td>
                <td className="px-4 py-3 text-gray-400 text-xs">{p.brand ?? '—'}</td>
                <td className="px-4 py-3 text-gray-400 text-xs">{p.category_name ?? '—'}</td>
                <td className="px-4 py-3 text-gray-300">{formatCurrency(p.price_fils)}</td>
                <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">
                  {p.size_ml ? `${p.size_ml} ml` : '—'}
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs font-medium ${p.stock === 0 ? 'text-red-400' : p.stock < 5 ? 'text-yellow-400' : 'text-gray-300'}`}>
                    {p.stock}
                  </span>
                </td>
                <td className="px-4 py-3">
                  {canEdit ? (
                    <button
                      onClick={() => toggleHouseBrand.mutate({ id: p.id, is_house_brand: !p.is_house_brand })}
                      title={p.is_house_brand ? 'YHC / Fursan house brand — AI prefers' : 'Not a house brand'}
                    >
                      {p.is_house_brand ? <ToggleRight size={20} className="text-[#C41E3A]" /> : <ToggleLeft size={20} className="text-gray-600" />}
                    </button>
                  ) : (
                    p.is_house_brand ? <ToggleRight size={20} className="text-[#C41E3A]" /> : <ToggleLeft size={20} className="text-gray-600" />
                  )}
                </td>
                <td className="px-4 py-3">
                  {canEdit ? (
                    <button onClick={() => toggleActive.mutate({ id: p.id, active: !p.active })}>
                      {p.active ? <ToggleRight size={20} className="text-[#C41E3A]" /> : <ToggleLeft size={20} className="text-gray-600" />}
                    </button>
                  ) : (
                    p.active ? <ToggleRight size={20} className="text-[#C41E3A]" /> : <ToggleLeft size={20} className="text-gray-600" />
                  )}
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  {p.edited_at ? (
                    <div className="flex flex-col leading-tight">
                      <span className="text-xs text-gray-300" title={parseUtc(p.edited_at).toLocaleString('en-GB', { timeZone: 'Asia/Amman' })}>
                        {formatRelativeEdit(p.edited_at)}
                      </span>
                      <span className="text-[10px] text-gray-500 truncate max-w-[9rem]" title={p.edited_by_email ?? ''}>
                        {p.edited_by_email ? p.edited_by_email.split('@')[0] : (p.edited_by_admin_id === null ? 'till' : '—')}
                      </span>
                    </div>
                  ) : (
                    <span className="text-xs text-gray-600">—</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    {canEdit && (
                      <button
                        onClick={() => setModalProduct(p)}
                        className="p-1.5 text-gray-500 hover:text-white transition-colors"
                        title="Edit product"
                      >
                        <Pencil size={14} />
                      </button>
                    )}
                    <RouterLink
                      to={`/stock-ledger?product_id=${p.id}`}
                      className="p-1.5 text-gray-500 hover:text-white transition-colors"
                      title="Stock ledger — full history"
                    >
                      <FileBarChart size={14} />
                    </RouterLink>
                    {canDelete && (
                      <button
                        onClick={() => setConfirmDelete([p])}
                        className="p-1.5 text-gray-500 hover:text-red-400 transition-colors"
                        title="Delete product"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-[#2e2e2e] text-xs text-gray-500">
            <span>{data?.total} total products</span>
            <div className="flex gap-2">
              <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1 rounded bg-[#2e2e2e] disabled:opacity-40">Prev</button>
              <span className="px-2 py-1">{page} / {totalPages}</span>
              <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)} className="px-3 py-1 rounded bg-[#2e2e2e] disabled:opacity-40">Next</button>
            </div>
          </div>
        )}
      </div>

      {modalProduct !== null && (
        <ProductModal
          product={modalProduct === 'new' ? null : modalProduct}
          categories={cats?.categories ?? []}
          onClose={() => setModalProduct(null)}
          onSaved={() => { qc.invalidateQueries({ queryKey: ['products'] }); setModalProduct(null) }}
        />
      )}

      {confirmDelete !== null && (
        <DeleteConfirmModal
          products={confirmDelete}
          onClose={() => setConfirmDelete(null)}
          onDone={() => {
            qc.invalidateQueries({ queryKey: ['products'] })
            setConfirmDelete(null)
            clearSelection()
          }}
        />
      )}
    </div>
  )
}

// Delete confirmation. Single or bulk — handled by the products array
// length. Explains the hard vs soft split ("with past sales" = kept as
// history, deactivated) so ops isn't surprised when a delete leaves
// old-line-item products around.
function DeleteConfirmModal({
  products, onClose, onDone,
}: {
  products: Product[]
  onClose: () => void
  onDone: () => void
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<{ hard: number; soft: number; not_found: number } | null>(null)

  const isBulk = products.length > 1

  async function submit() {
    setBusy(true); setError(null)
    try {
      if (isBulk) {
        const res = await api.post('/products/bulk-delete', { ids: products.map(p => p.id) })
        const r = (res.data as { totals: { hard: number; soft: number; not_found: number } }).totals
        setResult(r)
      } else {
        const res = await api.delete(`/products/${products[0].id}`)
        const r = res.data as { deleted: 'hard' | 'soft'; message?: string }
        setResult({ hard: r.deleted === 'hard' ? 1 : 0, soft: r.deleted === 'soft' ? 1 : 0, not_found: 0 })
      }
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } }).response?.data?.message
      setError(msg ?? 'Delete failed. Please try again.')
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4">
      <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl w-full max-w-md">
        <div className="p-5 space-y-4">
          <div className="flex items-start gap-3">
            <div className="mt-0.5"><AlertTriangle size={20} className="text-red-400" /></div>
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-white">
                {isBulk ? `Delete ${products.length} products?` : `Delete "${products[0].name}"?`}
              </h3>
              <p className="text-xs text-gray-400 mt-1 leading-relaxed">
                This <strong className="text-red-400">can't be undone</strong>. Products with past sales are kept in the
                database for order history but deactivated (removed from browse + POS); products that were never sold are
                fully deleted.
              </p>
            </div>
          </div>

          {isBulk && products.length <= 12 && (
            <ul className="rounded-lg bg-[#111] border border-[#2e2e2e] px-3 py-2 max-h-40 overflow-y-auto space-y-1">
              {products.map(p => (
                <li key={p.id} className="text-xs text-gray-300 flex items-center gap-2">
                  <span className="font-mono text-gray-500 shrink-0">{p.sku ?? '—'}</span>
                  <span className="truncate">{p.name}</span>
                </li>
              ))}
            </ul>
          )}
          {isBulk && products.length > 12 && (
            <p className="text-xs text-gray-500">
              {products.slice(0, 3).map(p => p.name).join(', ')} and {products.length - 3} more.
            </p>
          )}

          {result && (
            <div className="rounded-lg border border-green-500/40 bg-green-500/10 px-3 py-2 text-xs text-green-300">
              Done — {result.hard} permanently deleted, {result.soft} deactivated (had past sales), {result.not_found} not found.
            </div>
          )}
          {error && (
            <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">{error}</div>
          )}

          <div className="flex justify-end gap-3 pt-1">
            <button onClick={result ? onDone : onClose} className="px-4 py-2 text-sm text-gray-400 hover:text-white">
              {result ? 'Close' : 'Cancel'}
            </button>
            {!result && (
              <button
                onClick={submit}
                disabled={busy}
                className="px-4 py-2 bg-[#C41E3A] hover:bg-[#a01830] disabled:opacity-50 text-white text-sm rounded-lg flex items-center gap-1.5"
              >
                <Trash2 size={14} />
                {busy ? 'Deleting…' : (isBulk ? `Delete ${products.length} products` : 'Delete product')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function ProductModal({ product, categories, onClose, onSaved }: {
  product: Product | null; categories: Category[]; onClose: () => void; onSaved: () => void
}) {
  const { register, handleSubmit, watch, setValue, formState: { errors, isSubmitting } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: product ? {
      sku: product.sku,
      name: product.name,
      name_ar: product.name_ar ?? '',
      brand: product.brand ?? '',
      barcode: product.barcode ?? '',
      price_fils: product.price_fils,
      cost_fils: product.cost_fils,
      category_id: product.category_id,
      stock: product.stock,
      size_ml: product.size_ml ?? undefined,
      alcohol_pct: product.alcohol_pct ?? undefined,
      description: product.description ?? '',
      description_ar: product.description_ar ?? '',
      tasting_notes_en: product.tasting_notes_en ?? '',
      tasting_notes_ar: product.tasting_notes_ar ?? '',
      image_key: product.image_key ?? '',
      variant_group_id: product.variant_group_id ?? '',
      is_primary_variant: product.is_primary_variant === 1,
      is_house_brand: product.is_house_brand === 1,
    } : { is_primary_variant: true, is_house_brand: false },
  })

  const imageKey = watch('image_key') ?? ''
  const currentCategoryId = watch('category_id') ?? product?.category_id ?? ''

  // Per-category attribute state (migration 0150). Parsed from the
  // product row on load; falls back to {} when the product has no
  // values yet or is a fresh row. Schema comes from the category
  // definition — resolved live off the picked category so a category
  // change auto-swaps the fields.
  const [attributes, setAttributes] = useState<Record<string, string | number>>(() => {
    if (!product?.attributes_json) return {}
    try {
      const parsed = JSON.parse(product.attributes_json) as Record<string, string | number>
      return parsed && typeof parsed === 'object' ? parsed : {}
    } catch { return {} }
  })
  const activeCategory = categories.find(c => c.id === currentCategoryId)
  const activeSchema: AttributeField[] = (() => {
    const raw = activeCategory?.attribute_schema_json
    if (!raw) return []
    try { return JSON.parse(raw) as AttributeField[] } catch { return [] }
  })()

  // Autocomplete values for each attribute key — merge of the schema's
  // canonical `options` array and every distinct value already present
  // in the catalog for products in this category. Powers the datalist on
  // the free-text attribute inputs so ops can type freely but see existing
  // spellings as suggestions (avoids "Habana" vs "Habano" typo drift).
  // Only fetched when the active category has a schema; falls back to
  // schema `options` on error.
  const { data: attrValuesData } = useQuery<{ values: Record<string, string[]> }>({
    queryKey: ['category-attribute-values', currentCategoryId],
    queryFn: () => api.get(`/categories/${currentCategoryId}/attribute-values`).then(r => r.data),
    enabled: activeSchema.length > 0 && !!currentCategoryId,
    staleTime: 60_000,
  })
  const attrValues: Record<string, string[]> = attrValuesData?.values ?? {}

  const [submitError, setSubmitError] = useState<string | null>(null)
  // Reason ops types when editing the stock number, stamped into the
  // stock_movements ledger row (Phase 1 integrity fix 2026-09-29).
  // REQUIRED on edit-existing when stock actually moves — unless the
  // store_settings flag `stock_reason_required` is '0' (bulk-reconcile
  // escape hatch; server auto-stamps a dated default in that mode).
  // Ignored for new-product creates.
  const [stockChangeReason, setStockChangeReason] = useState('')
  const [stockReasonError, setStockReasonError] = useState<string | null>(null)
  // One-shot read of the admin settings — cheap, cached by react-query.
  const { data: settingsData } = useQuery<{ settings: Record<string, string> }>({
    queryKey: ['admin-settings-for-stock-reason'],
    queryFn:  () => api.get('/settings').then(r => r.data),
    staleTime: 30_000,
  })
  const stockReasonRequired = (settingsData?.settings?.stock_reason_required ?? '1') !== '0'
  const currentStock = watch('stock')
  const stockChanged = product ? Number(currentStock ?? 0) !== product.stock : false
  async function onSubmit(data: FormData) {
    setSubmitError(null)
    setStockReasonError(null)
    if (
      product &&
      stockChanged &&
      stockReasonRequired &&
      stockChangeReason.trim().length === 0
    ) {
      setStockReasonError('A reason is required when changing the stock number.')
      return
    }
    try {
      // Strip empty values before send so we don't persist `{key: ''}`.
      const cleanAttrs = Object.fromEntries(
        Object.entries(attributes).filter(([_, v]) => v !== '' && v !== null && v !== undefined)
      )
      const payload: Record<string, unknown> = { ...data, attributes: cleanAttrs }
      if (product && stockChanged) {
        payload.stock_change_reason = stockChangeReason.trim()
      }
      if (product) {
        await api.put(`/products/${product.id}`, payload)
      } else {
        await api.post('/products', payload)
      }
      onSaved()
    } catch (e: unknown) {
      const resp = (e as { response?: { data?: { message?: string; code?: string } } }).response?.data
      // Show the worker's specific message when available (e.g. duplicate
      // barcode collision surfaces as 409 with the offending SKU name).
      setSubmitError(resp?.message ?? 'Could not save the product. Please try again.')
    }
  }

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4">
      <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#2e2e2e]">
          <div>
            <h2 className="text-sm font-semibold text-white">{product ? 'Edit Product' : 'Add Product'}</h2>
            {product?.sku && <p className="text-xs text-gray-500 mt-0.5">{product.sku}</p>}
          </div>
          <button onClick={onClose}><X size={16} className="text-gray-400" /></button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-5 space-y-4">
          {/* Image */}
          <div>
            <label className="block text-xs text-gray-400 mb-1.5">Product Image</label>
            <ImageUploader
              value={imageKey}
              onChange={(key) => setValue('image_key', key)}
            />
          </div>

          <FieldRow label="Name (EN)" error={errors.name?.message}>
            <Input {...register('name')} placeholder="Johnnie Walker Black Label" />
          </FieldRow>
          <FieldRow label="Name (AR)">
            <Input {...register('name_ar')} placeholder="جوني ووكر بلاك ليبل" dir="rtl" />
          </FieldRow>
          <div className="grid grid-cols-2 gap-4">
            <FieldRow label="Brand">
              <Input {...register('brand')} placeholder="Johnnie Walker" />
            </FieldRow>
            <FieldRow label="Size (ml)">
              <Input {...register('size_ml', { valueAsNumber: true })} type="number" placeholder="750" />
            </FieldRow>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <FieldRow label="SKU" error={errors.sku?.message}>
              <Input
                {...register('sku')}
                placeholder={product ? product.sku : 'Auto-generated on create'}
                autoCapitalize="characters"
              />
            </FieldRow>
            <FieldRow label="Barcode (EAN-13 / UPC)" error={errors.barcode?.message}>
              <Input {...register('barcode')} placeholder="Scan or type" inputMode="numeric" />
            </FieldRow>
          </div>
          {product && (
            <p className="text-[11px] text-gray-500 -mt-2">
              Renaming SKU affects POS scanner lookups and receipt references. Leave blank to keep {product.sku}.
            </p>
          )}
          <div className="grid grid-cols-3 gap-4">
            <FieldRow label="Price (fils)" error={errors.price_fils?.message}>
              <Input {...register('price_fils', { valueAsNumber: true })} type="number" placeholder="18500" />
            </FieldRow>
            <FieldRow label="Cost (fils)" error={errors.cost_fils?.message}>
              <Input {...register('cost_fils', { valueAsNumber: true })} type="number" placeholder="0" />
            </FieldRow>
            <FieldRow label="ABV (%)">
              <Input {...register('alcohol_pct', { valueAsNumber: true })} type="number" step="0.1" placeholder="40" />
            </FieldRow>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <FieldRow label="Stock" error={errors.stock?.message}>
              <Input {...register('stock', { valueAsNumber: true })} type="number" placeholder="0" />
            </FieldRow>
            <FieldRow label="Category" error={errors.category_id?.message}>
              <select {...register('category_id')} className="w-full bg-[#242424] border border-[#3a3a3a] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C41E3A]">
                <option value="">Select category</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </FieldRow>
          </div>
          {product && stockChanged && stockReasonRequired && (
            <FieldRow
              label="Reason for stock change (required)"
              error={stockReasonError ?? undefined}
              hint={`${product.stock} → ${currentStock ?? 0}. This note is stamped on the ledger for reconciliation reports.`}
            >
              <Input
                type="text"
                value={stockChangeReason}
                onChange={(e) => {
                  setStockChangeReason(e.target.value)
                  if (stockReasonError && e.target.value.trim().length > 0) setStockReasonError(null)
                }}
                placeholder="e.g. Aligned to Great Plains 2026-10-01 physical count"
                maxLength={200}
              />
            </FieldRow>
          )}
          {product && stockChanged && !stockReasonRequired && (
            <p className="text-xs text-amber-500 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2">
              Bulk-reconciliation mode active ({product.stock} → {currentStock ?? 0}). This change will be stamped on the ledger as today's physical count reconciliation.
            </p>
          )}
          <FieldRow label="Description (EN)">
            <textarea {...register('description')} rows={3} className="w-full bg-[#242424] border border-[#3a3a3a] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C41E3A] resize-none" />
          </FieldRow>
          <FieldRow label="Description (AR)">
            <textarea {...register('description_ar')} rows={3} dir="rtl" className="w-full bg-[#242424] border border-[#3a3a3a] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C41E3A] resize-none" />
          </FieldRow>

          {activeSchema.length > 0 && (
            <div className="border-t border-[#2e2e2e] pt-4">
              <p className="text-xs text-gray-500 mb-3">
                {activeCategory?.name} — category attributes (Migration 0150). Filter-eligible fields drive the customer browse filter chips; display-only fields render as specs on the product detail. Leave blank to hide from the specs block.
              </p>
              <div className="grid grid-cols-2 gap-3">
                {activeSchema.map((field) => {
                  const raw = attributes[field.key]
                  const val = raw === undefined || raw === null ? '' : String(raw)
                  const set = (v: string) => {
                    setAttributes(prev => {
                      const next = { ...prev }
                      if (v === '' || v === null) {
                        delete next[field.key]
                      } else if (field.type === 'number') {
                        const num = Number(v)
                        if (Number.isFinite(num)) next[field.key] = num
                        else delete next[field.key]
                      } else {
                        next[field.key] = v
                      }
                      return next
                    })
                  }
                  return (
                    <div key={field.key} className={field.type === 'text' ? 'col-span-2' : ''}>
                      <label className="block text-xs text-gray-400 mb-1">
                        {field.label_en}
                        {field.filterable && (
                          <span className="ml-1.5 text-[10px] uppercase tracking-wide text-[#C41E3A]">Filterable</span>
                        )}
                      </label>
                      {field.type === 'enum' ? (
                        // Free-text input with datalist autocomplete (2026-08-22
                        // enhancement request from Ehab). Suggestions come from
                        // the merged (schema options ∪ existing catalog values)
                        // list fetched via /admin/categories/:id/attribute-values.
                        // Ops can type any value — the schema's original options
                        // list is no longer a hard constraint — but the datalist
                        // nudges toward existing spellings to prevent typo drift.
                        <>
                          <Input
                            list={`attr-datalist-${field.key}`}
                            value={val}
                            onChange={e => set(e.target.value)}
                            placeholder="—"
                            autoComplete="off"
                          />
                          <datalist id={`attr-datalist-${field.key}`}>
                            {(attrValues[field.key] ?? field.options ?? []).map(opt => (
                              <option key={opt} value={opt} />
                            ))}
                          </datalist>
                        </>
                      ) : field.type === 'number' ? (
                        <Input
                          type="number"
                          step="any"
                          value={val}
                          onChange={e => set(e.target.value)}
                        />
                      ) : (
                        <Input
                          value={val}
                          onChange={e => set(e.target.value)}
                        />
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          <div className="border-t border-[#2e2e2e] pt-4">
            <p className="text-xs text-gray-500 mb-3">
              Tasting notes — one attribute per line as <code className="bg-[#1a1a1a] px-1 rounded">Aroma: warm vanilla, oak.</code> The customer widget bolds the prefix before the colon. Lines without a colon render as plain prose. Leave blank to hide the section.
            </p>
            <FieldRow label="Tasting notes (EN)">
              <textarea {...register('tasting_notes_en')} rows={4} placeholder={'Aroma: warm vanilla, charred oak.\nPalate: caramel, gentle spice.\nFinish: long, smoky.\nFood pairing: dark chocolate.'} className="w-full bg-[#242424] border border-[#3a3a3a] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C41E3A] resize-none font-mono" />
            </FieldRow>
            <FieldRow label="Tasting notes (AR)">
              <textarea {...register('tasting_notes_ar')} rows={4} dir="rtl" placeholder={'الرائحة: فانيليا دافئة، بلوط محمّص.\nالمذاق: كراميل، توابل خفيفة.\nالخاتمة: طويلة، مدخّنة.\nيقدّم مع: شوكولاتة داكنة.'} className="w-full bg-[#242424] border border-[#3a3a3a] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C41E3A] resize-none font-mono" />
            </FieldRow>
          </div>

          <div className="border-t border-[#2e2e2e] pt-4">
            <p className="text-xs text-gray-500 mb-3">Size Variants — group related sizes under a shared ID so the app shows a size selector on the product page.</p>
            <FieldRow label="Variant Group ID">
              <Input {...register('variant_group_id')} placeholder="e.g. wine-chateau-musar-red" />
            </FieldRow>
            <div className="mt-3 flex items-center gap-2">
              <input type="checkbox" id="is_primary" {...register('is_primary_variant')} className="accent-[#C41E3A]" />
              <label htmlFor="is_primary" className="text-xs text-gray-400">Primary variant (shown in browse listings)</label>
            </div>
          </div>

          <div className="border-t border-[#2e2e2e] pt-4">
            <div className="flex items-center gap-2">
              <input type="checkbox" id="is_house_brand" {...register('is_house_brand')} className="accent-[#C41E3A]" />
              <label htmlFor="is_house_brand" className="text-xs text-gray-400">
                House brand (YHC / Fursan) — preferred by DrinX AI cart recommendations
              </label>
            </div>
          </div>

          {product && <ReviewsModerationSection productId={product.id} />}

          {submitError && (
            <div className="rounded-lg border border-[#C41E3A]/60 bg-[#C41E3A]/10 px-3 py-2 text-xs text-red-300">
              {submitError}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-400 hover:text-white">Cancel</button>
            <button type="submit" disabled={isSubmitting} className="px-4 py-2 bg-[#C41E3A] hover:bg-[#a51830] disabled:opacity-50 text-white text-sm rounded-lg">
              {isSubmitting ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function ImageUploader({ value, onChange }: { value: string; onChange: (key: string) => void }) {
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const imageUrl = value ? `${FILES_URL}/${value}` : null

  async function handleFile(file: File) {
    setError(null)
    setUploading(true)
    try {
      const fd = new FormData()
      fd.append('file', file)
      const res = await api.post<{ key: string }>('/products/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      onChange(res.data.key)
    } catch {
      setError('Upload failed. Please try again.')
    } finally {
      setUploading(false)
    }
  }

  return (
    <div>
      {imageUrl && (
        <div className="relative group mb-2 rounded-lg overflow-hidden bg-[#242424] h-44 flex items-center justify-center">
          <img
            src={imageUrl}
            alt=""
            className="max-h-full max-w-full object-contain"
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
          />
          <a
            href={imageUrl}
            target="_blank"
            rel="noreferrer"
            className="absolute top-2 right-2 bg-black/60 hover:bg-black/80 text-white rounded-md p-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
            title="Open full size"
          >
            <ExternalLink size={14} />
          </a>
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f) }}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className="flex items-center gap-2 px-3 py-2 bg-[#242424] hover:bg-[#2e2e2e] disabled:opacity-50 border border-[#3a3a3a] rounded-lg text-sm text-gray-300 transition-colors w-full justify-center"
      >
        <Upload size={14} />
        {uploading ? 'Uploading…' : imageUrl ? 'Change Image' : 'Upload Image'}
      </button>
      {error && <p className="text-xs text-red-400 mt-1">{error}</p>}
    </div>
  )
}

function SortTh({ label, col, current, dir, onSort }: {
  label: string; col: string; current: string; dir: 'asc' | 'desc'; onSort: (c: string) => void
}) {
  const active = current === col
  return (
    <th className="px-4 py-3 text-left">
      <button onClick={() => onSort(col)} className="flex items-center gap-1 text-xs font-medium text-gray-600 hover:text-white transition-colors group">
        {label}
        <span className={active ? 'text-[#C41E3A]' : 'text-gray-700 group-hover:text-gray-500'}>
          {active ? (dir === 'asc' ? '↑' : '↓') : '↕'}
        </span>
      </button>
    </th>
  )
}

// "Last edited" cell — compact relative-time formatter.
// Bare `Date.now()` diff works fine on client (unlike workflow scripts;
// scoping is different) since we're in the browser event loop.
function formatRelativeEdit(iso: string): string {
  const then = new Date(iso).getTime()
  const now  = Date.now()
  const diffMs = Math.max(0, now - then)
  const min  = Math.floor(diffMs / 60_000)
  if (min < 1)    return 'just now'
  if (min < 60)   return `${min}m ago`
  const hr = Math.floor(min / 60)
  if (hr < 24)    return `${hr}h ago`
  const day = Math.floor(hr / 24)
  if (day < 7)    return `${day}d ago`
  if (day < 30)   return `${Math.floor(day / 7)}w ago`
  // For anything older, fall through to a compact date so ops sees the
  // actual month/year — relative "9 months ago" doesn't help
  // reconciliation, absolute "2026-03-14" does.
  return new Date(iso).toISOString().slice(0, 10)
}

function FieldRow({ label, error, hint, children }: { label: string; error?: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs text-gray-400 mb-1.5">{label}</label>
      {children}
      {hint && !error && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
      {error && <p className="text-xs text-red-400 mt-1">{error}</p>}
    </div>
  )
}

const Input = ({ className = '', ...props }: React.InputHTMLAttributes<HTMLInputElement>) => (
  <input {...props} className={`w-full bg-[#242424] border border-[#3a3a3a] rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#C41E3A] ${className}`} />
)

// ── Reviews moderation ───────────────────────────────────────────────────────

interface AdminReview {
  id: string
  rating: number
  review_text: string | null
  display_name: string | null
  hidden: number
  created_at: string
  user_id: string
  user_full_name: string | null
  user_phone: string | null
}

function ReviewsModerationSection({ productId }: { productId: string }) {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery<{ reviews: AdminReview[]; total: number }>({
    queryKey: ['admin-product-reviews', productId],
    queryFn: () => api.get(`/products/${productId}/reviews?limit=50`).then(r => r.data),
  })

  const setHidden = useMutation({
    mutationFn: ({ reviewId, hidden }: { reviewId: string; hidden: boolean }) =>
      api.patch(`/products/${productId}/reviews/${reviewId}`, { hidden }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-product-reviews', productId] }),
  })

  const deleteReview = useMutation({
    mutationFn: (reviewId: string) =>
      api.delete(`/products/${productId}/reviews/${reviewId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-product-reviews', productId] }),
  })

  const reviews = data?.reviews ?? []

  return (
    <div className="border-t border-[#2e2e2e] pt-4">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-gray-500 uppercase tracking-wider">Customer reviews</p>
        <span className="text-xs text-gray-600">{data?.total ?? 0} total</span>
      </div>

      {isLoading && <p className="text-xs text-gray-600">Loading…</p>}

      {!isLoading && reviews.length === 0 && (
        <p className="text-xs text-gray-600">No reviews yet.</p>
      )}

      <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
        {reviews.map(r => (
          <div key={r.id} className={`rounded-lg px-3 py-2.5 border ${r.hidden ? 'border-amber-500/30 bg-amber-500/5' : 'border-[#2e2e2e] bg-[#1a1a1a]'}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-yellow-400 text-xs">{'★'.repeat(r.rating)}{'☆'.repeat(5 - r.rating)}</span>
                  <span className="text-xs text-gray-300 font-medium truncate">
                    {r.display_name?.trim() || r.user_full_name || 'Customer'}
                  </span>
                  {r.hidden === 1 && (
                    <span className="text-[10px] uppercase bg-amber-500/20 text-amber-300 px-1.5 py-0.5 rounded">
                      Hidden
                    </span>
                  )}
                </div>
                {r.review_text && (
                  <p className="text-xs text-gray-300 whitespace-pre-wrap">{r.review_text}</p>
                )}
                <p className="text-[10px] text-gray-600 mt-1 font-mono">
                  {new Date(r.created_at).toISOString().slice(0, 10)} · {r.user_phone ?? '—'}
                </p>
              </div>
              <div className="flex flex-col gap-1 shrink-0">
                <button
                  type="button"
                  onClick={() => setHidden.mutate({ reviewId: r.id, hidden: r.hidden !== 1 })}
                  className="text-[11px] text-gray-400 hover:text-white border border-[#3a3a3a] rounded px-2 py-0.5"
                >
                  {r.hidden === 1 ? 'Unhide' : 'Hide'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm('Delete this review permanently?')) {
                      deleteReview.mutate(r.id)
                    }
                  }}
                  className="text-[11px] text-red-400 hover:text-red-300 border border-red-500/20 rounded px-2 py-0.5"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

